import { memo, useCallback, useRef, useEffect, useState } from 'react';

import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import { TRequiresTimeChange } from '@/types/session.types';
import Button from '@ui/buttons/button/button';
import Popover from '@ui/popover/popover';
import { useAppSelector } from '@/store/hooks';
import { selectIsSavingWorkingProject } from '@/store/slices/project/selectors';
import getUtcOffset from '@/utils/get-utc-offset';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import OptionalComponent from '@ui/optional-component/optional-component';
import { DateRange } from '@ui/calendar/types';
import { getUserTimezone } from '@ui/calendar/utils';
import DateRangePickerHorizontal from '@ui/calendar/date-range-picker-horizontal';

/* Do not use css module for this because we need to target the css from outside of this component */
import './project-date.css';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';

type ProjectDateProps = {
  timestamp?: number | null;
  end_timestamp?: number | null;
  selectedTimezone?: string;
  onSave?: (data: DateRange) => void;
  onChange?: (data: DateRange) => void;
  onClose?: () => void;
  requiresTimeChange?: TRequiresTimeChange | null;
  disabled?: boolean;
  dateWidth?: string;
  timeWidth?: string;
  showUTCOffset?: boolean;
  isInEditMode?: boolean;
  hideLabel?: boolean;
};

const formatDate = (timestamp: number, timeZone?: string): string => {
  // get date
  const date = new Date(timestamp);
  return date.toLocaleDateString(navigator.language, { year: 'numeric', month: 'short', day: 'numeric', timeZone });
};

const formatTime = (timestamp: number, timeZone?: string): string => {
  // get time
  const date = new Date(timestamp);
  return date.toLocaleTimeString(navigator.language, { hour: 'numeric', minute: '2-digit', hour12: true, timeZone });
};

const ProjectDate = memo(
  ({
    disabled,
    timestamp,
    end_timestamp,
    onSave,
    onChange,
    requiresTimeChange,
    dateWidth = '124px',
    timeWidth = '84px',
    onClose,
    showUTCOffset,
    isInEditMode,
    selectedTimezone,
    hideLabel,
  }: ProjectDateProps) => {
    const savingWorkingProject = useAppSelector(selectIsSavingWorkingProject);

    const [startDateOpen, setStartDateOpen] = useState(false);
    const [endDateOpen, setEndDateOpen] = useState(false);
    const TIMEZONE = useRef(selectedTimezone || getUserTimezone());

    useEffect(() => {
      // intentionally check for false instead if !isInEditMode
      // because we only want to control this if the prop is actually passed in
      if (isInEditMode === false) {
        setStartDateOpen(false);
        setEndDateOpen(false);
      }
    }, [isInEditMode]);

    const handleChange = useCallback(
      (dates: DateRange) => {
        onChange?.(dates);
      },
      [onChange]
    );

    const getStartDate = useCallback(() => {
      const placeholderLabel = 'Set date';
      if (!timestamp || requiresTimeChange?.start_date) {
        return placeholderLabel;
      }

      return formatDate(timestamp, selectedTimezone || getUserTimezone());
    }, [requiresTimeChange, timestamp, selectedTimezone]);

    const getStartTime = useCallback(() => {
      const placeholderLabel = 'Set time';
      if (!timestamp || requiresTimeChange?.start_time) {
        return placeholderLabel;
      }

      return formatTime(timestamp, selectedTimezone || TIMEZONE.current);
    }, [requiresTimeChange?.start_time, selectedTimezone, timestamp]);

    const getEndDate = useCallback(() => {
      const placeholderLabel = 'Set date';
      if (!end_timestamp || requiresTimeChange?.end_date) {
        return placeholderLabel;
      }

      return formatDate(end_timestamp, selectedTimezone || TIMEZONE.current);
    }, [end_timestamp, requiresTimeChange?.end_date, selectedTimezone]);

    const getEndTime = useCallback(() => {
      const placeholderLabel = 'Set time';
      if (!end_timestamp || requiresTimeChange?.end_time) {
        return placeholderLabel;
      }

      return formatTime(end_timestamp, selectedTimezone || TIMEZONE.current);
    }, [end_timestamp, requiresTimeChange?.end_time, selectedTimezone]);

    return (
      <div data-component="ProjectDate">
        <OptionalComponent display={!hideLabel}>
          <Text className={`L3 margin-b-8 padding-l-8`} color="var(--gray-300)">
            BASIC INFO
          </Text>
        </OptionalComponent>
        <div className="flex fd-row jc-center ai-center gap-4 height-inherit">
          <Container background="var(--gray-900)" className="flex-1 radius-8 padding-v-8 padding-r-8 padding-l-0">
            <div className="flex fd-row jc-flex-start ai-center padding-l-12">
              <div className="flex-column jc-center ai-center height-42">
                <div className="square-6 radius-full edit-project-date__startCircle" />
                <div className="edit-project-date__line" />
                <div className="square-6 radius-full edit-project-date__endCircle" />
              </div>

              <div className="flex-column gap-4 flex-1">
                <div className="flex fd-row jc-flex-start ai-center margin-l-8">
                  <Text color="var(--gray-300)" className="L3 flex-1">
                    Start
                  </Text>

                  <div className="flex gap-2">
                    <Popover
                      disabled={disabled}
                      closeOnClick={false}
                      forcePosition="bottom-left"
                      onClose={() => {
                        setStartDateOpen(false);
                        onClose?.();
                      }}
                      overflow="visible"
                      border="none"
                      horizontalOffset={-13} // should align with the right-edge of the main container
                      verticalOffset={46} // should align with the bottom of the main container
                      contentClassName="margin-t-2"
                      open={!!startDateOpen}
                      ignoreClasses={['.react-select-menu-container']}
                      renderTriggerButton={() => (
                        <Button
                          className="clear-button flex fd-row jc-center ai-center gap-4 S3 edit-project-date__startButton"
                          backgroundColor="transparent"
                          onClick={() => {
                            setStartDateOpen((prev) => !prev);
                          }}
                        >
                          <Container
                            className="padding-v-6 padding-h-12 S3 text-left edit-project-date__subButton"
                            borderRadius="8px 0 0 8px"
                            width={dateWidth}
                            height="32px"
                            background="var(--gray-800)"
                            hoverBackground="var(--gray-700)"
                          >
                            {getStartDate()}
                          </Container>

                          <Container
                            className="padding-v-6 padding-h-12 S3 edit-project-date__subButton"
                            background="var(--gray-800)"
                            width={timeWidth}
                            height="32px"
                            hoverBackground="var(--gray-700)"
                            borderRadius="0 8px 8px 0"
                          >
                            {getStartTime()}
                          </Container>
                        </Button>
                      )}
                      width="fit-content"
                    >
                      {({ handleClose }) => (
                        <div className="width-100-percent position-relative">
                          <DateRangePickerHorizontal
                            overflowClass="overflow-visible"
                            onChange={handleChange}
                            defaultValue={{
                              startDate: timestamp || null,
                              endDate: end_timestamp || null,
                              selectedTimezone: selectedTimezone || TIMEZONE.current,
                            }}
                            requiresTimeChange={requiresTimeChange}
                            renderFooter={({ startDate, endDate, setStartDate, setEndDate }) => {
                              if (!onSave) return null;
                              return (
                                <Container className="flex fd-row jc-flex-end ai-center padding-t-16 gap-8">
                                  <Button
                                    backgroundColor="var(--gray-600)"
                                    hoverBackgroundColor="var(--gray-500)"
                                    height="40px"
                                    className="L1"
                                    onClick={() => {
                                      setStartDate(null);
                                      setEndDate(null);
                                      handleClose();
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    width="73px"
                                    height="40px"
                                    disabled={!startDate || !endDate || (startDate && endDate && startDate > endDate)}
                                    onClick={() => {
                                      if (!startDate || !endDate) return;

                                      // ensure seconds are set to 0
                                      startDate.setSeconds(0);
                                      endDate.setSeconds(0);

                                      onSave({
                                        startDate: startDate.getTime(),
                                        endDate: endDate.getTime(),
                                        selectedTimezone: selectedTimezone || TIMEZONE.current,
                                      });
                                      handleClose();
                                    }}
                                    className="flex ai-center"
                                    backgroundColor={getThemeColorCSSVariable()}
                                  >
                                    <Text
                                      className="L1"
                                      color={getThemeColorContrastCSSVariable(
                                        EColorVariablesV2.buttonText,
                                        EColorVariablesV2.primary
                                      )}
                                    >
                                      Save
                                    </Text>
                                  </Button>
                                </Container>
                              );
                            }}
                          />
                        </div>
                      )}
                    </Popover>
                  </div>
                </div>

                <div className="flex fd-row jc-flex-start ai-center margin-l-8">
                  <Text color="var(--gray-300)" className="L3 flex-1">
                    End
                  </Text>
                  <div className="flex gap-4">
                    <Popover
                      disabled={disabled}
                      closeOnClick={false}
                      forcePosition="bottom-left"
                      onClose={() => {
                        setEndDateOpen(false);
                        onClose?.();
                      }}
                      overflow="visible"
                      horizontalOffset={-13} // should align with the right-edge of the main container
                      verticalOffset={10} // should align with the bottom of the main container
                      contentClassName="margin-t-2"
                      open={!!endDateOpen}
                      ignoreClasses={['.react-select-menu-container']}
                      renderTriggerButton={() => (
                        <Button
                          className="clear-button flex fd-row jc-center ai-center gap-4 S3 edit-project-date__endButton"
                          backgroundColor="transparent"
                          onClick={() => {
                            setEndDateOpen((prev) => !prev);
                          }}
                        >
                          <Container
                            className="padding-v-6 padding-h-12 S3 text-left edit-project-date__subButton"
                            borderRadius="8px 0 0 8px"
                            width={dateWidth}
                            height="32px"
                            background="var(--gray-800)"
                            hoverBackground="var(--gray-700)"
                          >
                            {getEndDate()}
                          </Container>

                          <Container
                            className="padding-v-6 padding-h-12 S3 edit-project-date__subButton"
                            background="var(--gray-800)"
                            width={timeWidth}
                            height="32px"
                            hoverBackground="var(--gray-700)"
                            borderRadius="0 8px 8px 0"
                          >
                            {getEndTime()}
                          </Container>
                        </Button>
                      )}
                      width="fit-content"
                    >
                      {({ handleClose }) => (
                        <div className="width-100-percent position-relative">
                          <DateRangePickerHorizontal
                            overflowClass="overflow-visible"
                            onChange={handleChange}
                            defaultValue={{
                              startDate: timestamp || null,
                              endDate: end_timestamp || null,
                              selectedTimezone: selectedTimezone || TIMEZONE.current,
                            }}
                            renderFooter={({ startDate, endDate, setStartDate, setEndDate }) => {
                              if (!onSave) return null;
                              return (
                                <Container className="flex fd-row jc-flex-end ai-center padding-t-16 gap-8">
                                  <Button
                                    backgroundColor="var(--gray-600)"
                                    hoverBackgroundColor="var(--gray-500)"
                                    height="40px"
                                    className="L1"
                                    onClick={() => {
                                      setStartDate(null);
                                      setEndDate(null);
                                      handleClose();
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    width="73px"
                                    height="40px"
                                    loading={savingWorkingProject}
                                    disabled={
                                      savingWorkingProject ||
                                      !startDate ||
                                      !endDate ||
                                      (startDate && endDate && startDate > endDate)
                                    }
                                    onClick={() => {
                                      if (!startDate || !endDate) return;

                                      // ensure seconds are set to 0
                                      startDate.setSeconds(0);
                                      endDate.setSeconds(0);

                                      onSave({
                                        startDate: startDate.getTime(),
                                        endDate: endDate.getTime(),
                                        selectedTimezone: selectedTimezone || TIMEZONE.current,
                                      });
                                      handleClose();
                                    }}
                                    className="flex ai-center"
                                    hoverOpacity={0.8}
                                    backgroundColor={getThemeColorCSSVariable()}
                                  >
                                    <Text
                                      color={getThemeColorContrastCSSVariable(
                                        EColorVariablesV2.buttonText,
                                        EColorVariablesV2.primary
                                      )}
                                      className="L1"
                                    >
                                      Save
                                    </Text>
                                  </Button>
                                </Container>
                              );
                            }}
                          />
                        </div>
                      )}
                    </Popover>
                  </div>
                </div>
              </div>
            </div>
          </Container>

          <OptionalComponent display={showUTCOffset}>
            <Container
              background="var(--gray-900)"
              className="flex-column radius-12 padding-v-12 padding-h-16"
              height="84px"
              width="112px"
            >
              <Icon name={ICONS.GLOBE_OUTLINE} size={16} color="var(--gray-400)" />
              <Text className="S2 margin-t-6 margin-b-2">{getUtcOffset(timestamp || Date.now())}</Text>

              <TooltipPortal
                tooltipContent={
                  <Text color="var(--gray-true-dark-100)" className="C2">
                    {TIMEZONE.current}
                  </Text>
                }
                tailSize={0}
                delayStart={150}
                animationType="slide-up"
                keepOpenOnContentHover={false}
                verticalOffset={-8}
              >
                <Text clamp={1} color="var(--gray-300)" className="C1">
                  {TIMEZONE.current}
                </Text>
              </TooltipPortal>
            </Container>
          </OptionalComponent>
        </div>
      </div>
    );
  }
);

export default ProjectDate;
